import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// ---------------------------------------------------------------------------
// Error boundary (class component — React requirement)
// ---------------------------------------------------------------------------

type ErrorBoundaryProps = {
  children: ReactNode;
  title: string;
  /** Localized fallback text shown when a child throws. */
  fallbackText: string;
  /** Localized retry button label. */
  retryLabel: string;
};
type ErrorBoundaryState = { error: string | null; epoch: number };

class WidgetErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { error: null, epoch: 0 };

  static getDerivedStateFromError(err: Error): Partial<ErrorBoundaryState> {
    return { error: err.message };
  }

  override componentDidCatch(err: Error, info: ErrorInfo): void {
    console.error(`[WidgetError] ${this.props.title}:`, err, info);
  }

  retry = (): void => {
    // Bumping `epoch` re-keys the children, forcing a fresh mount so the
    // child has a chance to recover from a transient error (worker crash,
    // momentary bad spec, etc.) without the user reloading the page.
    this.setState((s) => ({ error: null, epoch: s.epoch + 1 }));
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          className="flex flex-col items-center justify-center h-full p-4 gap-3 text-sm"
          style={{ color: 'var(--color-danger)' }}
          role="alert"
        >
          <span>{this.props.fallbackText}</span>
          <button
            type="button"
            onClick={this.retry}
            className="px-3 py-1 rounded border text-xs"
            style={{
              borderColor: 'var(--color-danger)',
              color: 'var(--color-danger)',
            }}
          >
            {this.props.retryLabel}
          </button>
        </div>
      );
    }
    return <div key={this.state.epoch} className="contents">{this.props.children}</div>;
  }
}

// ---------------------------------------------------------------------------
// Loading spinner
// ---------------------------------------------------------------------------

/** Simple CSS spinner — no external assets. */
function LoadingOverlay(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0 flex items-center justify-center"
      style={{ background: 'color-mix(in srgb, var(--color-bg) 70%, transparent)' }}>
      <div className="spinner" aria-label={t('common.loading')} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// WidgetContainer
// ---------------------------------------------------------------------------

type WidgetContainerProps = {
  title: string;
  isLoading?: boolean;
  error?: string | null;
  children: ReactNode;
  className?: string;
  /** Show the drag handle in the title bar. Default: true. */
  draggable?: boolean;
  /** When provided, renders a × button in the title bar. */
  onRemove?: (() => void) | undefined;
  /** When provided, renders a settings gear button in the title bar. */
  onSettings?: (() => void) | undefined;
  /** Extra content rendered at the right side of the title bar (before action buttons). */
  titleBarExtra?: ReactNode;
  /** Optional note rendered as a footer below the widget body. */
  note?: string | undefined;
};

/**
 * Generic wrapper for all dashboard widgets.
 * Provides: title bar, loading overlay, error boundary, consistent sizing.
 */
export function WidgetContainer({
  title,
  isLoading = false,
  error,
  children,
  className = '',
  draggable = true,
  onRemove,
  onSettings,
  titleBarExtra,
  note,
}: WidgetContainerProps): JSX.Element {
  const { t } = useTranslation();
  const body = (
    <WidgetErrorBoundary
      title={title}
      fallbackText={t('errors.widgetFailed')}
      retryLabel={t('common.retry')}
    >
      {error ? (
        <div
          className="flex items-center justify-center h-full p-4 text-sm"
          style={{ color: 'var(--color-danger)' }}
          role="alert"
        >
          {error}
        </div>
      ) : (
        children
      )}
    </WidgetErrorBoundary>
  );

  return (
    <section
      className={`relative flex flex-col h-full rounded-lg border overflow-hidden ${className}`}
      style={{
        background: 'var(--color-bg)',
        borderColor: 'var(--color-border)',
      }}
    >
        {/* Title bar */}
        <header
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border-b shrink-0"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-fg)',
          }}
        >
          {draggable && (
            <span
              className="widget-drag-handle flex items-center shrink-0"
              style={{ cursor: 'grab', color: 'var(--color-muted)' }}
              aria-label={t('widgetActions.dragHandle')}
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
                <circle cx="4" cy="3" r="1.2" />
                <circle cx="10" cy="3" r="1.2" />
                <circle cx="4" cy="7" r="1.2" />
                <circle cx="10" cy="7" r="1.2" />
                <circle cx="4" cy="11" r="1.2" />
                <circle cx="10" cy="11" r="1.2" />
              </svg>
            </span>
          )}
          <span className="truncate flex-1">{title}</span>
          {titleBarExtra}
          {onSettings && (
            <button
              type="button"
              onClick={onSettings}
              aria-label={t('widgetActions.settings')}
              title={t('widgetActions.settings')}
              className="shrink-0 flex items-center justify-center w-5 h-5 rounded hover:opacity-80"
              style={{ color: 'var(--color-muted)' }}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                {/* Sliders / adjustments — clearer than a small gear */}
                <line x1="2" y1="4" x2="14" y2="4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="10.5" cy="4" r="1.7" fill="var(--color-surface)" stroke="currentColor" strokeWidth="1.5" />
                <line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="5" cy="8" r="1.7" fill="var(--color-surface)" stroke="currentColor" strokeWidth="1.5" />
                <line x1="2" y1="12" x2="14" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="10.5" cy="12" r="1.7" fill="var(--color-surface)" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`${t('widgetActions.remove')}: ${title}`}
              className="shrink-0 flex items-center justify-center w-5 h-5 rounded hover:opacity-80"
              style={{ color: 'var(--color-muted)' }}
            >
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path
                  d="M2 2 L10 10 M10 2 L2 10"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </header>

        <div className="relative flex-1 min-h-0">
          {body}
          {isLoading && <LoadingOverlay />}
        </div>

        {note && (
          <footer
            className="px-2.5 py-1 border-t shrink-0 italic leading-snug"
            style={{
              fontSize: 11,
              background: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-muted)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {note}
          </footer>
        )}
      </section>
  );
}
